import React, { useEffect, useState, useRef } from "react";
import { Card, Button, Space, Select, message } from "antd";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import ApiService from "../../core/service/api-service";
import { useModal } from "../../core/context/ModalContext";
import AppointmentSchedulerFilter from "./AppointmentSchedulerFilter";
import moment from "moment";
import WorklistItemStatusEnum, { getStatusLabel, getStatusOptions, getStatusColor } from "../../enums/WorklistItemStatusEnum";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import AppointmentDetails from "./AppointmentDetails";

export default function AppointmentScheduler() {
    const { t } = useTranslation();
    const [deviceId, setDeviceId] = useState(null);
    const [dateRange, setDateRange] = useState({
            start: moment().startOf('week').toDate(),
            end: moment().endOf('week').toDate()
        }); 
    const [events, setEvents] = useState([]);
    const calendarRef = useRef(null);
    const { openModal } = useModal();
    const {hasPermission} = useUser();

    useEffect(() => {
        if (deviceId && dateRange) {
            loadAppointments();
        }
    }, [deviceId, dateRange]);

    const handleDatesSet = (dateInfo) => {
        const { start, end } = dateInfo;

        
        const prevStart = dateRange.start.getTime();
        const prevEnd = dateRange.end.getTime();
        const newStart = start.getTime();
        const newEnd = end.getTime();

        // Eğer tarih aralığı değişmediyse hiçbir şey yapma
        if (prevStart === newStart && prevEnd === newEnd) {
            return;
        }
        
        setDateRange({
            start: start,
            end: end
        });
    }

    const loadAppointments = async () => {
        if (!deviceId || !dateRange) return;
        
        try {
            const response = await ApiService.Appointment.GetByDevice(
                deviceId,
                true,
                moment(dateRange.start).startOf('day').utc().toISOString(),
                moment(dateRange.end).endOf('day').utc().toISOString()
            );

            if (response.success) {
                const calendarEvents = response.data.map(appointment => {
                    const firstWorklistItem = appointment?.worklistItems?.[0];
                    const patientName = firstWorklistItem
                        ? `${firstWorklistItem.patientName} ${firstWorklistItem.patientLastname}`
                        : 'Unknown';

                    const statusColor = getStatusColor(appointment.status);

                    return {
                        id: appointment.id,
                        title: patientName,
                        start: appointment.scheduledStartTime,
                        end: appointment.scheduledEndTime,
                        backgroundColor: statusColor,
                        borderColor: statusColor,
                        extendedProps: {
                            appointment: appointment
                        }
                    };
                });
                setEvents(calendarEvents);
            }
        }
        catch (error) {
            console.error('Failed to load appointments:', error);
        }
    }

    const handleEventClick = (info) => {
        const appointment = info.event.extendedProps.appointment;
        

        const handleStatusChange = async (newStatus) => {
            try {
                loadAppointments();
            } catch (error) {
                console.error('Failed to update appointment status:', error);
            }
        };

        openModal(t('Appointment Details'), (
            <AppointmentDetails 
                appointmentId={appointment.id}
                onStatusChange={handleStatusChange} 
                onRemove={() => {
                    loadAppointments();
                }}
            />
        ));
    }

    const handleEventDrop = async (info) => {
        try {
            const appointmentId = info.event.id;
            const appointment = info.event.extendedProps.appointment;
            
            const response = await ApiService.Appointment.Update({
                id: appointmentId,
                deviceId: appointment.deviceId,
                scheduledStartTime: moment(info.event.start).utc().format('YYYY-MM-DDTHH:mm:ss'),
                scheduledEndTime: moment(info.event.end).utc().format('YYYY-MM-DDTHH:mm:ss')
            });

            if (response.success) {
                message.success(t('Appointment time updated successfully'));
                // Randevuları yeniden yükle
                loadAppointments();
            } else {
                // Başarısızsa eski pozisyona geri döndür
                info.revert();
            }
        } catch (error) {
            console.error('Failed to update appointment:', error);
            message.error(t('Failed to update appointment'));
            // Hata durumunda eski pozisyona geri döndür
            info.revert();
        }
    }

    return (
        <>
            <Card
                title={t('Filter')}
                style={{ marginBottom: 16 }}
            >
                <AppointmentSchedulerFilter
                    deviceId={deviceId}
                    setDeviceId={setDeviceId}
                />
            </Card>

            <Card
                title={t('Appointments')}
            >
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                    }}
                    datesSet={handleDatesSet}
                    editable={true}
                    eventDurationEditable={true}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventDrop}
                    selectable={false}
                    scrollTime={moment().format('HH:mm:ss')}
                    selectMirror={true}
                    slotDuration={'00:15:00'}
                    slotLabelInterval={'00:15:00'}
                    slotLabelFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false, // AM/PM'i kapatır, 24 saat formatına geçer
                        meridiem: false // "am/pm" yazısını gizler
                    }}
                    dayMaxEvents={true}
                    weekends={true}
                    events={events}
                    eventClick={handleEventClick}
                    height="600px"
                    allDaySlot={false}
                    locale="tr"
                    buttonText={{
                        today: t('Today'),
                        month: t('Month'),
                        week: t('Week'),
                        day: t('Day'),
                        list: t('List')
                    }}
                />
            </Card>
        </>
    )
}
